import type { TNotificationMessageType } from '../types/notification.types'

/**
 * Категория уведомления для группировки в матрице настроек.
 * - `work` — рабочие задачи (НС, заявки, документооборот).
 * - `info` — информационные (события календаря, отчёты, технические).
 * - `hidden` — служебные типы, не показываются в настройках (info-рассылки от системы).
 */
export type TNotificationCategory = 'work' | 'info' | 'hidden'

/**
 * Семантический «цвет» уведомления для подложки аватара и иконки.
 * Совпадает со встроенными цветами Vuetify-темы, используется для классов и v-icon color.
 */
export type TNotificationVisualColor = 'error' | 'warning' | 'info' | 'primary'

/**
 * Визуальное описание типа уведомления.
 */
export interface INotificationVisual {
  icon: string
  color: TNotificationVisualColor
  /** Текстовый лейбл типа для матрицы настроек и подзаголовков fallback. */
  label: string
  category: TNotificationCategory
  /** Тип, у которого пользователь не имеет права отключать все каналы доставки. */
  isCritical: boolean
}

/**
 * Соответствие message_type → визуальное описание для UI.
 * Семантика цвета: красный — критичные рабочие (НС/заявки/задачи), жёлтый — события
 * календаря и отчёты, голубой — технические и системные info.
 * Иконки — только Remix Icons (ri-*) по соглашению проекта.
 */
const VISUAL_MAP: Record<TNotificationMessageType, INotificationVisual> = {
  adverse_event: {
    icon: 'ri-spam-2-line',
    color: 'error',
    label: 'Мои нежелательные события',
    category: 'work',
    isCritical: true,
  },
  global_adverse_event: {
    icon: 'ri-spam-2-line',
    color: 'error',
    label: 'Все нежелательные события',
    category: 'work',
    isCritical: false,
  },
  ticket: {
    icon: 'ri-error-warning-line',
    color: 'error',
    label: 'Мои заявки',
    category: 'work',
    isCritical: true,
  },
  global_ticket: {
    icon: 'ri-error-warning-line',
    color: 'error',
    label: 'Все заявки',
    category: 'work',
    isCritical: false,
  },
  electronic_signature: {
    icon: 'ri-tools-line',
    color: 'error',
    label: 'Электронные подписи',
    category: 'work',
    isCritical: true,
  },
  training: {
    icon: 'ri-graduation-cap-line',
    color: 'primary',
    label: 'Обучение СМК',
    category: 'hidden',
    isCritical: false,
  },
  responsibility_entry: {
    icon: 'ri-tools-line',
    color: 'error',
    label: 'Мои задачи',
    category: 'work',
    isCritical: true,
  },
  calendar_event: {
    icon: 'ri-calendar-event-line',
    color: 'warning',
    label: 'Мои события календаря',
    category: 'info',
    isCritical: false,
  },
  daily_report: {
    icon: 'ri-time-line',
    color: 'warning',
    label: 'Дневные отчёты',
    category: 'info',
    isCritical: false,
  },
  technical: {
    icon: 'ri-settings-2-line',
    color: 'info',
    label: 'Технические уведомления',
    category: 'info',
    isCritical: false,
  },
  info: {
    icon: 'ri-information-line',
    color: 'info',
    label: 'Информационные сообщения',
    category: 'hidden',
    isCritical: false,
  },
  appeal: {
    icon: 'ri-mail-line',
    color: 'info',
    label: 'Обращения граждан',
    category: 'work',
    isCritical: true,
  },
}

/** Дефолт для неизвестных типов (на случай если бек добавит новый тип, а фронт не успел обновиться). */
const DEFAULT_VISUAL: INotificationVisual = {
  icon: 'ri-notification-2-line',
  color: 'primary',
  label: 'Уведомление',
  category: 'info',
  isCritical: false,
}

/**
 * Возвращает визуал для типа уведомления.
 * Для неизвестного типа отдаёт дефолтный «нейтральный» визуал.
 */
export function getNotificationVisual(messageType: string | undefined): INotificationVisual {
  if (!messageType) return DEFAULT_VISUAL

  return VISUAL_MAP[messageType as TNotificationMessageType] ?? DEFAULT_VISUAL
}

/**
 * true для типов, у которых пользователь не имеет права отключать все каналы доставки.
 * Используется при валидации матрицы в notificationSettingsStore.
 */
export function isCriticalType(messageType: string | undefined): boolean {
  return getNotificationVisual(messageType).isCritical
}
